import React, { FC } from 'react'
import LaneArrowIcon from 'transfix-icons/build/LaneArrowIcon'
import { Button } from 'transfix-ui/components/Button'
import { Card } from 'transfix-ui/components/Card'
import { FormattedDateTime } from 'transfix-ui/components/FormattedDateTime'
import { Typography, Box, makeStyles } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme/colors'
import { DedicatedLanesResponse } from 'apps/marketplace/apiTypes/dedicatedLanes'
import { formatCityState } from 'utils/formatCityState'
import { getDedicatedLaneInfo, parseLoadingMethod } from './utils'

export interface ILaneOfferCardInfoProps {
  dedicatedLane: DedicatedLanesResponse
  onClick?: () => void
}

const useLaneOfferCardInfoStyles = makeStyles(() => ({
  card: {
    padding: '12px',
    marginBottom: '12px',
    border: `1px solid ${brandColors.coolGray2}`,
    boxShadow: `0 1px 2px rgba(0, 0, 0, 0.15)`,
  },
  locationBox: {
    width: '420px',
  },
  loadsBox: {
    width: '150px',
  },
  datesBox: {
    width: '220px',
  },
  location: {
    width: '200px',
  },
}))

export const DedicatedLanesOfferCardInfo: FC<ILaneOfferCardInfoProps> = ({
  dedicatedLane,
  onClick,
}) => {
  const {
    deliveryCity,
    deliveryLoadingMethod,
    deliveryStateCode,
    endDate,
    equipmentName,
    monthDuration,
    pickupCity,
    pickupLoadingMethod,
    pickupStateCode,
    startDate,
    volume,
  } = getDedicatedLaneInfo(dedicatedLane) || {}

  const classes = useLaneOfferCardInfoStyles()

  return (
    <div>
      <Card className={classes.card}>
        <Box display='flex' justifyContent='space-between' alignItems='center'>
          <Box display='flex' className={classes.locationBox}>
            <Box pl={2} className={classes.location}>
              <Typography variant='subtitle1' data-test='lane-offer-row-pickup'>
                {formatCityState(pickupCity || '', pickupStateCode || '')}
              </Typography>
              <Typography
                variant='body2'
                color='textSecondary'
                data-test='lane-offer-row-pickup-loading-method'>
                {parseLoadingMethod(pickupLoadingMethod)}
              </Typography>
            </Box>
            <LaneArrowIcon size='large' />
            <Box pl={2} className={classes.location}>
              <Typography variant='subtitle1' data-test='lane-offer-row-pickup'>
                {formatCityState(deliveryCity || '', deliveryStateCode || '')}
              </Typography>
              <Typography
                variant='body2'
                color='textSecondary'
                data-test='lane-offer-row-pickup-loading-method'>
                {parseLoadingMethod(deliveryLoadingMethod)}
              </Typography>
            </Box>
          </Box>
          <Box className={classes.loadsBox}>
            <Box pl={3}>
              <Typography display='inline' variant='subtitle1' data-test='lane-offer-row-volume'>
                {volume} loads
              </Typography>
              <Typography display='inline' color='textSecondary'>
                {' / week'}
              </Typography>
              <Typography variant='body2' color='textSecondary'>
                {equipmentName}
              </Typography>
            </Box>
          </Box>
          <Box className={classes.datesBox}>
            <Box pl={2}>
              <Typography variant='subtitle1'>
                <FormattedDateTime
                  displayTimezone='America/New_York'
                  datetime={startDate}
                  format='dateAndYear'
                />
                {' - '}
                <FormattedDateTime
                  displayTimezone='America/New_York'
                  datetime={endDate}
                  format='dateAndYear'
                />
              </Typography>
              {monthDuration && (
                <Typography variant='body2' color='textSecondary'>
                  {monthDuration} {monthDuration > 1 ? 'months' : 'month'}
                </Typography>
              )}
            </Box>
          </Box>
          <Box>
            <Button color='tertiary' onClick={onClick}>
              View Details
            </Button>
          </Box>
        </Box>
      </Card>
    </div>
  )
}
